import type { MusicSyncState } from '../../../../vrroom_typescript/src/platform/contracts';

/** 进度对齐容差（秒）：与视频屏同步（VideoScreenSystem）一致。 */
const SYNC_SEEK_TOLERANCE = 0.5;

/**
 * 房间背景音乐播放器（主持人经 room:music-control 控制，服务端权威）：
 * 单个 HTMLAudioElement 全局循环播放，全场等音量（不做距离衰减，与
 * Agora 远端语音的 SpatialMixer 互不相干）。同步手法与 VideoScreenSystem
 * 同源——seq 单调丢乱序/重复、mediaTime+anchoredAt 锚点按 serverTime
 * 外推、循环对 duration 取模、0.5s 容差内不 seek；自动播放策略用
 * 「muted 先行 + 首次点屏回正」降级（无手势端 unmute 播放会被浏览器
 * 拒绝）。曲目 URL/名称随服务端同步态下发，无需查曲目库。
 */
export class BgmPlayer {
    private audio = new Audio();
    /** 最近一次套用的 seq：乱序/重复事件丢弃。 */
    private syncSeq?: number;
    /** 自动播放策略降级标记：unmute 播放被拒后本地先静音，首次点屏回正。 */
    private mutedFallback = false;
    private mutedFallbackWatch?: () => void;
    /** 元数据未就绪时暂存的同步态，loadedmetadata 后补套用。 */
    private pendingSync?: { state: MusicSyncState; serverNow: number };
    /** element.volume 可写性（iOS Safari 只读、设置被忽略）：探测一次。 */
    private volumeWritable?: boolean;
    /** iOS 音量增益链（element.volume 只读时启用）：媒体源→增益→输出。 */
    private ctx?: AudioContext;
    private gain?: GainNode;
    private ctxResumeWatch?: () => void;

    constructor() {
        this.audio.loop = true;
        // metadata：赋 src 即拉取元数据（loadedmetadata → pendingSync 补套 →
        // 起播）。绝不可用 none——资源选择后抓取挂起，loadedmetadata 永不
        // 触发，pendingSync 死锁、play() 无人调用（点击播放全场无声的根因）。
        this.audio.preload = 'metadata';
        this.audio.addEventListener('loadedmetadata', () => {
            const pending = this.pendingSync;
            this.pendingSync = undefined;
            if (pending) this.applySync(pending.state, pending.serverNow, true);
        });
    }

    /** 服务端同步态套用（room:music 事件 / 快照 music 块）：幂等——与当前
     *  一致的维度不动，进度差超容差才 seek。force 绕过 seq 守卫（元数据
     *  就绪后的补套用）。换房/被踢经 reset() 清空本地态，seq 重新计数。
     *  自动播放降级（本地静音）期间到达的同步态不强行回正——无手势时程序
     *  unmute 会被浏览器再次暂停；保持静音起播，由首次点屏的手势回正。 */
    applySync(state: MusicSyncState, serverNow: number, force = false): void {
        if (!force && this.syncSeq !== undefined && state.seq <= this.syncSeq) return;
        this.syncSeq = state.seq;
        this.applyVolume(state.volume);
        // 停止：暂停并释放源（挂起网络请求）。
        if (!state.trackUrl) {
            this.pendingSync = undefined;
            if (!this.audio.paused) this.audio.pause();
            if (this.audio.getAttribute('src')) {
                this.audio.removeAttribute('src');
                this.audio.load();
            }
            return;
        }
        // 换曲：切源后 currentTime 归零、时长未知，进度经 pendingSync 在
        // loadedmetadata 后对齐；曲目未变则原位续播/暂停。
        if (this.audio.getAttribute('src') !== state.trackUrl) {
            this.pendingSync = { state, serverNow };
            this.audio.src = state.trackUrl;
            return;
        }
        const duration = Number.isFinite(this.audio.duration) ? this.audio.duration : 0;
        if (duration <= 0) {
            this.pendingSync = { state, serverNow };
        } else {
            this.applyCore(state, serverNow, duration);
        }
    }

    /** 换房/被踢：清空本地同步态（seq 重新计数）并停止播放；静音降级一并
     *  回正（此刻无源，程序 unmute 不会触发自动播放策略）。 */
    reset(): void {
        this.syncSeq = undefined;
        this.pendingSync = undefined;
        this.mutedFallback = false;
        this.detachMutedFallbackWatch();
        this.audio.muted = false;
        if (!this.audio.paused) this.audio.pause();
        if (this.audio.getAttribute('src')) {
            this.audio.removeAttribute('src');
            this.audio.load();
        }
    }

    destroy(): void {
        this.reset();
        if (this.ctxResumeWatch) {
            document.removeEventListener('pointerdown', this.ctxResumeWatch);
            this.ctxResumeWatch = undefined;
        }
        void this.ctx?.close().catch(() => {});
        this.ctx = undefined;
        this.gain = undefined;
    }

    /** 音量落地：桌面端 element.volume 直接生效（零新增链路）；iOS Safari
     *  的 element.volume 只读（设置被忽略、读回恒为 1），探测不可写时改走
     *  Web Audio 增益（createMediaElementSource → GainNode → 输出，增益在
     *  iOS 上可用的标准解法）。AudioContext 在无手势端创建即挂起：首次
     *  点屏 resume（与静音降级回正同窗口）。同源音频无 CORS 静音风险。 */
    private applyVolume(volume: number): void {
        if (this.volumeWritable === undefined) {
            const prev = this.audio.volume;
            const probe = volume > 0.5 ? volume - 0.3 : volume + 0.3;
            this.audio.volume = probe;
            this.volumeWritable = Math.abs(this.audio.volume - probe) < 1e-6;
            this.audio.volume = prev;
        }
        if (this.volumeWritable) {
            this.audio.volume = volume;
            return;
        }
        if (!this.gain && typeof AudioContext !== 'undefined') {
            try {
                const ctx = new AudioContext();
                const gain = ctx.createGain();
                ctx.createMediaElementSource(this.audio).connect(gain).connect(ctx.destination);
                this.ctx = ctx;
                this.gain = gain;
                if (ctx.state === 'suspended') {
                    this.ctxResumeWatch = () => { void ctx.resume().catch(() => {}); };
                    document.addEventListener('pointerdown', this.ctxResumeWatch, { once: true });
                }
            } catch { /* Web Audio 不可用：退回 element.volume（iOS 外均有效） */ }
        }
        if (this.gain) {
            // 增益链接管后 element.volume 保持 1，避免与增益相乘双重衰减。
            this.audio.volume = 1;
            this.gain.gain.value = volume;
        } else {
            this.audio.volume = volume;
        }
        if (this.ctx?.state === 'suspended') void this.ctx.resume().catch(() => {});
    }

    private applyCore(state: MusicSyncState, serverNow: number, duration: number): void {
        let target = state.mediaTime +
            (state.playing ? Math.max(0, serverNow - state.anchoredAt) / 1000 : 0);
        if (duration > 0 && target > duration) target = target % duration;
        if (Math.abs(this.audio.currentTime - target) > SYNC_SEEK_TOLERANCE)
            this.audio.currentTime = Math.max(0, Math.min(duration, target));
        if (state.playing) {
            if (this.audio.paused) this.play();
        } else if (!this.audio.paused) {
            this.audio.pause();
        }
    }

    private play(): void {
        void this.audio.play().catch(() => {
            // 自动播放策略拒绝（无手势客户端）：静音重试保起播（背景音乐
            // 从不有意静音），用户首次点屏后回正音量。
            if (this.audio.muted) return;
            this.mutedFallback = true;
            this.audio.muted = true;
            void this.audio.play().then(() => this.watchMutedFallback()).catch(() => {
                this.mutedFallback = false;
                this.audio.muted = false;
            });
        });
    }

    /** 静音降级回正：用户首次点屏（获得手势）即恢复音量并清除标记——
     *  回正必须在手势上下文里做，程序性 unmute 会被浏览器再次暂停。
     *  一次性监听，reset/destroy 时移除。 */
    private watchMutedFallback(): void {
        if (this.mutedFallbackWatch) return;
        this.mutedFallbackWatch = () => {
            this.mutedFallbackWatch = undefined;
            if (!this.mutedFallback) return;
            this.mutedFallback = false;
            this.audio.muted = false;
        };
        document.addEventListener('pointerdown', this.mutedFallbackWatch, { once: true });
    }

    private detachMutedFallbackWatch(): void {
        if (!this.mutedFallbackWatch) return;
        document.removeEventListener('pointerdown', this.mutedFallbackWatch);
        this.mutedFallbackWatch = undefined;
    }
}
